import { useEffect, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { ChevronRight, PanelLeft } from 'lucide-react';
import Sidebar from './Sidebar';
import QueuePausedBanner from './QueuePausedBanner';
import EnvSwitcher from './EnvSwitcher';
import { HeaderActionsProvider } from './HeaderActions';
import { Button } from './ui/button';
import { Separator } from './ui/separator';
import { TooltipProvider } from './ui/tooltip';
import { useEnvironment } from '../hooks/useEnvironment';
import { MOBILE_QUERY, useMediaQuery } from '../hooks/useMediaQuery';

/** Breadcrumb trail per route — the design shows "Section › Page" in the header
 *  and keeps the full page title as the H1 inside the page body. */
const CRUMBS: Record<string, string[]> = {
  '/':                       ['Dashboard'],
  '/price-checks/review':    ['Price Checks', 'Review'],
  '/price-checks/queue':     ['Price Checks', 'Queue'],
  '/price-checks/processed': ['Price Checks', 'Processed'],
  '/home-value/review':      ['Home Value', 'Review'],
  '/home-value/queue':       ['Home Value', 'Queue'],
  '/home-value/processed':   ['Home Value', 'Processed'],
  '/home-value/no-data':     ['Home Value', 'No data'],
  '/new-items/queue':        ['New Items', 'Queue'],
  '/new-items/processed':    ['New Items', 'Processed'],
  '/all-items':              ['All Items'],
  '/admin/automation':       ['Admin', 'Automation'],
  '/admin/jobs':             ['Admin', 'Job Queue'],
  '/admin/vendors':          ['Admin', 'Vendors'],
  '/admin/users':            ['Admin', 'Users'],
};

function resolveCrumbs(pathname: string): string[] {
  if (CRUMBS[pathname]) return CRUMBS[pathname];
  if (pathname.startsWith('/all-items/')) return ['All Items', 'Item Detail'];
  return ['Greenlight'];
}

const SIDEBAR_KEY = 'gl_sidebar';

export default function Layout({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const { isStaging } = useEnvironment();
  const isMobile = useMediaQuery(MOBILE_QUERY);

  // Desktop preference is sticky; mobile always starts closed so the overlay
  // never covers the page on load.
  const [open, setOpen] = useState(
    () => !window.matchMedia(MOBILE_QUERY).matches && localStorage.getItem(SIDEBAR_KEY) !== 'closed',
  );

  // Crossing the breakpoint re-derives the state rather than only forcing it
  // shut: widening the window has to bring the sidebar back, otherwise it stays
  // hidden with no explanation until the user finds the toggle.
  useEffect(() => {
    setOpen(isMobile ? false : localStorage.getItem(SIDEBAR_KEY) !== 'closed');
  }, [isMobile]);

  function changeOpen(next: boolean) {
    setOpen(next);
    if (!isMobile) localStorage.setItem(SIDEBAR_KEY, next ? 'open' : 'closed');
  }

  const crumbs = resolveCrumbs(pathname);
  const showHeaderToggle = isMobile || !open;

  return (
    <TooltipProvider delayDuration={200}>
      <div className="relative flex h-screen flex-col overflow-hidden">
        {/* Sandbox marker — a hazard stripe across the very top, so the
            environment is obvious even when the header is scrolled past. */}
        {isStaging && (
          <div
            className="h-[3px] shrink-0"
            style={{
              background: 'repeating-linear-gradient(135deg, #f59e0b 0 8px, #fbbf24 8px 16px)',
            }}
          />
        )}

        <div className="flex flex-1 overflow-hidden bg-background">
          <Sidebar open={open} onOpenChange={changeOpen} overlay={isMobile} />

          <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
            <QueuePausedBanner />

            <HeaderActionsProvider>
              {slot => (
                <>
                  <header
                    className={cnHeader(isStaging)}
                  >
                    {showHeaderToggle && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => changeOpen(true)}
                        title="Toggle sidebar"
                        aria-label="Toggle sidebar"
                      >
                        <PanelLeft className="h-4 w-4" />
                      </Button>
                    )}

                    <EnvSwitcher />
                    <Separator orientation="vertical" />

                    <nav aria-label="Breadcrumb" className="flex min-w-0 items-center gap-3">
                      {crumbs.map((crumb, i) => (
                        <span key={crumb} className="flex min-w-0 items-center gap-3">
                          {i > 0 && <ChevronRight className="h-3.5 w-3.5 shrink-0 text-zinc-300" />}
                          <span
                            className={
                              i === crumbs.length - 1
                                ? 'truncate text-[13px] font-medium'
                                : 'truncate text-[13px] text-zinc-400'
                            }
                          >
                            {crumb}
                          </span>
                        </span>
                      ))}
                    </nav>

                    <div className="ml-auto flex items-center gap-2">{slot}</div>
                  </header>

                  {/* A height-constrained flex column, so a table page's
                      `flex-1 min-h-0` scroll area resolves against the viewport
                      and scrolls internally (sticky headers, pinned columns).
                      overflow-y-auto rather than hidden so ordinary long pages
                      — Dashboard, Automation, Item Detail — still scroll; with
                      hidden they were silently clipped at the fold. */}
                  {/* No bottom padding: on a table page the last thing in the
                      column is the pagination bar, which brings its own py-3 —
                      20px on top of that was dead space under the table, which
                      the review asked to reclaim. Pages that scroll their own
                      content (Dashboard, Automation, Item Detail) add `pb-5`
                      on their own root instead. */}
                  <main className="flex min-h-0 flex-1 flex-col overflow-y-auto px-5 pb-0 pt-5">
                    {children}
                  </main>
                </>
              )}
            </HeaderActionsProvider>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
}

/** Sandbox tints the header too — one glance tells you which system you are in. */
function cnHeader(isStaging: boolean): string {
  return [
    'flex h-14 shrink-0 items-center gap-3 border-b px-5',
    isStaging ? 'border-amber-200 bg-[#fffdf7]' : 'border-border bg-background',
  ].join(' ');
}
